/**
 * SmartSpeechEngine - Intelligent engine selection with fallback
 * 
 * Automatically selects the best available speech engine:
 * 1. StreamingSTT (if server available and WebSocket works)
 * 2. VADSpeechEngine (if VAD supported)
 * 3. BrowserSTT (fallback)
 * 4. MockSTT (for testing)
 */

import { SpeechEngine, SpeechStates } from './SpeechEngine.js';
import { MinimalStateMachine, MinimalStates, TransitionReasons } from './MinimalStateMachine.js';
import { StreamingSTT } from './StreamingSTT.js';
import { VADSpeechEngine } from './VADSpeechEngine.js';
import { BrowserSTT } from './BrowserSTT.js';
import { MockSTT } from './MockSTT.js';
import { EngineProbe } from './EngineProbe.js';
import { KWSFactory } from './kws/KWSFactory.js';

export class SmartSpeechEngine extends SpeechEngine {
    constructor(options = {}) {
        super();
        
        this.options = {
            // Engine preferences (in order of preference)
            preferredEngines: options.preferredEngines || ['streaming', 'vad', 'browser'],
            useMock: options.useMock || false,
            
            // Engine-specific options
            streamingOptions: options.streamingOptions || {},
            vadOptions: options.vadOptions || {},
            browserOptions: options.browserOptions || {},
            mockOptions: options.mockOptions || {},
            
            // Fallback settings
            testConnectionTimeout: options.testConnectionTimeout || 3000,
            autoFallback: options.autoFallback !== false,
            
            ...options
        };
        
        this.stateMachine = new MinimalStateMachine({
            maxHistorySize: options.maxHistorySize || 20
        });
        
        this.currentEngine = null;
        this.availableEngines = [];
        this.engineHistory = [];
        this.isInitialized = false;
        this.isSupported = false;
        
        // KWS (Keyword Spotting) system
        this.kws = null;
        this.kwsReady = false;
        this.wakePhrase = options.wakePhrase || 'hey chef';
        this.enableKWS = options.enableKWS !== false; // Enable by default
        this.kwsRefractoryMs = typeof options.kwsRefractoryMs === 'number' ? options.kwsRefractoryMs : 2000;
        this._lastWakeTs = 0;
        this.kwsEnabled = true;
        this.kwsHot = false;
        
        // Engine switch protocol state
        this.isSwitching = false;
        this.pendingSwitch = null;
        this.switchLock = null;
        
        // Only auto-initialize if explicitly requested
        if (options.autoInit) {
            this.initialize();
        }
    }

    /**
     * Initialize and test available engines
     */
    async initialize() {
        try {
            if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: Initializing...');
            
            // Test available engines first
            await this.testAvailableEngines();
            
            // Select best engine
            await this.selectBestEngine();
            
            // Initialize KWS if enabled (after engines are ready)
            if (this.enableKWS) {
                await this.initializeKWS();
            }
            
            this.isInitialized = true;
            this.isSupported = this.currentEngine !== null;
            
            if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: Initialized with engine:', 
                this.currentEngine ? this.currentEngine.constructor.name : 'none');
            
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Initialization failed:', error);
            this.isSupported = false;
        }
    }

    /**
     * Initialize KWS (Keyword Spotting) system
     */
    async initializeKWS() {
        try {
            if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: Initializing KWS...');
            
            this.kws = await KWSFactory.createKWS({
                wakePhrase: this.wakePhrase,
                sensitivity: 0.5,
                refractoryMs: this.kwsRefractoryMs
            });
            
            // Set up KWS detection callback
            if (this.kws.on) {
                this.kws.on('armed', () => {
                    if (this.emit) { try { this.emit('kws:armed'); } catch (_) {} }
                });
                this.kws.on('detected', (event) => {
                    this.handleKWSDetection(event);
                });
            } else if (this.kws.onDetect) {
                this.kws.onDetect((event) => {
                    this.handleKWSDetection(event);
                });
            }
            
            this.kwsReady = true;
            if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: KWS initialized successfully');
            
        } catch (error) {
            console.warn('🎤 SmartSpeechEngine: KWS initialization failed, continuing without KWS');
            this.kws = null;
            this.kwsReady = false;
            // Don't throw error, just disable KWS
        }
    }

    /**
     * Handle KWS detection event
     */
    handleKWSDetection(event) {
        const now = Date.now();
        if (now - this._lastWakeTs < this.kwsRefractoryMs) {
            return; // ignore double fires
        }
        this._lastWakeTs = now;
        // Play chime sound
        try {
            const path = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
            const isLive = path.indexOf('/live') === 0;
            this.playWakeChime(isLive ? 'live' : 'default');
        } catch (_) {
            this.playWakeChime('default');
        }
        
        // Start ASR if we're in ready state
        if (this.stateMachine.getCurrentState() === MinimalStates.READY) {
            // Stop KWS's internal BrowserSTT, then start main engine for full capture
            if (this.kws && this.kws.stop) {
                this.kws.stop().catch(() => {});
            }
            if (this.currentEngine) {
                this.kwsHot = true;
                this.currentEngine.start().then(() => {
                    this.startASR();
                }).catch((e) => console.warn('🎤 SmartSpeechEngine: Failed to start ASR on KWS:', e));
            }
        }
    }

    /**
     * Play wake word detection chime
     */
    playWakeChime(variant = 'default') {
        this.playOscillatorChime(variant);
    }

    /**
     * Play oscillator chime as fallback
     */
    playOscillatorChime(variant = 'default') {
        try {
            const micManagerModule = require('./MicManager.js');
            const mic = micManagerModule && micManagerModule.default ? micManagerModule.default : null;
            const audioContext = mic && mic.getAudioContext ? mic.getAudioContext() : new (window.AudioContext || window.webkitAudioContext)();
            if (audioContext && audioContext.state === 'suspended') {
                audioContext.resume().catch(() => {});
            }
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            // Slightly different sound for live session: quick two-step rising tone
            const start = audioContext.currentTime;
            if (variant === 'live') {
                oscillator.frequency.setValueAtTime(700, start);
                oscillator.frequency.linearRampToValueAtTime(950, start + 0.12);
                gainNode.gain.setValueAtTime(0.0001, start);
                gainNode.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
                gainNode.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
                oscillator.start(start);
                oscillator.stop(start + 0.2);
            } else {
                oscillator.frequency.setValueAtTime(880, start);
                gainNode.gain.setValueAtTime(0.0001, start);
                gainNode.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
                gainNode.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
                oscillator.start(start);
                oscillator.stop(start + 0.18);
            }
        } catch (error) {
            console.warn('🎤 SmartSpeechEngine: Failed to play chime:', error);
        }
    }

    /**
     * Start ASR after KWS detection
     */
    async startASR() {
        try {
            if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: Starting ASR after KWS detection...');
            
            // Transition to listening state
            await this.stateMachine.transitionTo(MinimalStates.LISTENING, TransitionReasons.KWS_DETECTED);
            
            // The speech engine is already running for partial results
            // Now we need to set up the proper callbacks for full ASR
            if (this.currentEngine) {
                // Set up callbacks for full ASR results
                this.currentEngine.setOnPartial((result) => {
                    if (this.kwsEnabled && !this.kwsHot) {
                        if (this.kws && this.kws.feedPartialTranscript) {
                            this.kws.feedPartialTranscript(result.transcript);
                        }
                        return;
                    }
                    if (this.onPartial) {
                        this.onPartial(result);
                    }
                });
                
                this.currentEngine.setOnFinal((result) => {
                    if (this.kwsEnabled && !this.kwsHot) {
                        if (this.kws && this.kws.feedPartialTranscript) {
                            this.kws.feedPartialTranscript(result.transcript);
                        }
                        return;
                    }
                    if (this.onFinal) {
                        this.onFinal(result);
                    }
                });
                
                if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: ASR now active for full recognition');
            }
            
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Failed to start ASR:', error);
        }
    }

    /**
     * Handle ASR endpoint and return to KWS armed state
     */
    async handleASREndpoint() {
        try {
            if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: ASR endpoint reached, returning to KWS armed state...');
            
            // Transition to endpointed first
            await this.stateMachine.transitionTo(MinimalStates.ENDPOINTED, TransitionReasons.LISTENING_ENDED);
            
            // If KWS is enabled, return to ready state (KWS armed)
            if (this.enableKWS && this.kws && this.kwsReady) {
                // Set up partial result handler for KWS again
                this.currentEngine.setOnPartial((result) => {
                    if (this.kws && this.kws.feedPartialTranscript) {
                        this.kws.feedPartialTranscript(result.transcript);
                    }
                });
                
                await this.stateMachine.transitionTo(MinimalStates.READY, TransitionReasons.KWS_ARMED);
                if (process.env.NODE_ENV === 'development') console.log('🎤 SmartSpeechEngine: Returned to KWS armed state');
            } else {
                // No KWS, stop the engine and go to idle
                if (this.currentEngine) {
                    await this.currentEngine.stop();
                }
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.STOPPING_COMPLETE);
            }
            
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Failed to handle ASR endpoint:', error);
        }
    }

    /**
     * Test all available engines
     */
    async testAvailableEngines() {
        this.availableEngines = [];
        const FORCE_BROWSER = true;
        if (FORCE_BROWSER) {
            // Force BrowserSTT only
            const engine = new BrowserSTT(this.options.browserOptions || {});
            this.availableEngines.push({ type: 'browser', engine, priority: 0 });
            return;
        }
        // Fallback to normal probing if flag removed later
        console.log('🔍 SmartSpeechEngine: Testing engines with probe system...');
        for (const engineType of this.options.preferredEngines) {
            try {
                const engineProbe = new EngineProbe();
                const isAvailable = await engineProbe.probe(engineType, this.getEngineOptions(engineType));
                if (isAvailable) {
                    const engine = await this.createEngine(engineType);
                    if (engine) {
                        this.availableEngines.push({ type: engineType, engine, priority: this.options.preferredEngines.indexOf(engineType) });
                    }
                }
            } catch (_) {}
        }
    }

    /**
     * Get the engine class for a given type
     */
    getEngineClass(engineType) {
        switch (engineType) {
            case 'streaming':
                return StreamingSTT;
            case 'vad':
                return VADSpeechEngine;
            case 'browser':
                return BrowserSTT;
            case 'mock':
                return MockSTT;
            default:
                return null;
        }
    }

    /**
     * Get engine-specific options for probing
     */
    getEngineOptions(engineType) {
        switch (engineType) {
            case 'streaming':
                return this.options.streamingOptions || {};
            case 'vad':
                return this.options.vadOptions || {};
            case 'browser':
                return this.options.browserOptions || {};
            case 'mock':
                return this.options.mockOptions || {};
            default:
                return {};
        }
    }

    /**
     * Create an engine instance (without testing)
     */
    async createEngine(engineType) {
        let engine = null;
        
        switch (engineType) {
            case 'streaming':
                if (this.options.useMock) return null;
                engine = new StreamingSTT(this.options.streamingOptions);
                break;
                
            case 'vad':
                if (this.options.useMock) return null;
                // Determine which speech engine to use for VAD
                const vadSpeechEngineType = this.getVADSpeechEngineType();
                engine = new VADSpeechEngine({
                    ...this.options.vadOptions,
                    speechEngineType: vadSpeechEngineType
                });
                break;
                
            case 'browser':
                if (this.options.useMock) return null;
                engine = new BrowserSTT(this.options.browserOptions);
                break;
                
            case 'mock':
                if (!this.options.useMock) return null;
                engine = new MockSTT(this.options.mockOptions);
                break;
                
            default:
                return null;
        }
        
        return engine;
    }

    /**
     * Create and test a specific engine
     */
    async createAndTestEngine(engineType) {
        let engine = null;
        
        switch (engineType) {
            case 'streaming':
                if (this.options.useMock) return null;
                engine = new StreamingSTT(this.options.streamingOptions);
                break;
                
            case 'vad':
                if (this.options.useMock) return null;
                // Determine which speech engine to use for VAD
                const vadSpeechEngineType = this.getVADSpeechEngineType();
                engine = new VADSpeechEngine({
                    ...this.options.vadOptions,
                    speechEngineType: vadSpeechEngineType
                });
                break;
                
            case 'browser':
                if (this.options.useMock) return null;
                engine = new BrowserSTT(this.options.browserOptions);
                break;
                
            case 'mock':
                if (!this.options.useMock) return null;
                engine = new MockSTT(this.options.mockOptions);
                break;
                
            default:
                return null;
        }
        
        // Test engine support using static method
        const EngineClass = this.getEngineClass(engineType);
        if (!EngineClass || !EngineClass.isSupported()) {
            if (engine) {
                try {
                    engine.destroy();
                } catch (error) {
                    console.warn(`Failed to destroy ${engineType} engine:`, error);
                }
            }
            return null;
        }
        
        // Additional check for VAD engine - make sure it's properly initialized
        if (engineType === 'vad' && engine) {
            try {
                // For VAD engine, just check if it can be created, don't test start/stop
                // as this might interfere with KWS initialization
                console.log('VAD engine created successfully');
            } catch (error) {
                console.warn('VAD engine failed initialization test:', error);
                if (engine) {
                    try {
                        engine.destroy();
                    } catch (destroyError) {
                        console.warn('Failed to destroy VAD engine:', destroyError);
                    }
                }
                return null;
            }
        }
        
        // Test connection for streaming engine
        if (engineType === 'streaming') {
            const connectionTest = await this.testStreamingConnection(engine);
            if (!connectionTest) {
                engine.destroy();
                return null;
            }
        }
        
        return engine;
    }

    /**
     * Test streaming engine connection
     */
    async testStreamingConnection(engine) {
        try {
            // Quick connection test
            const testPromise = new Promise((resolve) => {
                const timeout = setTimeout(() => resolve(false), this.options.testConnectionTimeout);
                
                // Try to create a session
                engine.createSession().then((success) => {
                    clearTimeout(timeout);
                    resolve(success);
                }).catch(() => {
                    clearTimeout(timeout);
                    resolve(false);
                });
            });
            
            return await testPromise;
        } catch (error) {
            return false;
        }
    }

    /**
     * Select the best available engine
     */
    async selectBestEngine() {
        if (this.availableEngines.length === 0) {
            console.warn('🎤 SmartSpeechEngine: No engines available');
            this.currentEngine = null;
            return;
        }
        
        // Sort by priority (lower number = higher priority)
        this.availableEngines.sort((a, b) => a.priority - b.priority);
        
        // Select the first (best) engine
        const selected = this.availableEngines[0];
        this.currentEngine = selected.engine;
        
        // Setup engine callbacks
        this.setupEngineCallbacks();
        
        // Record selection
        this.engineHistory.push({
            engine: selected.type,
            timestamp: Date.now(),
            reason: 'initial_selection'
        });
        
        console.log(`🎤 SmartSpeechEngine: Selected ${selected.type} engine`);
    }

    /**
     * Setup callbacks for current engine
     */
    setupEngineCallbacks() {
        if (!this.currentEngine) return;
        
        this.currentEngine.setOnPartial((result) => {
            if (this.onPartial) {
                this.onPartial(result);
            }
        });
        
        this.currentEngine.setOnFinal((result) => {
            if (this.onFinal) {
                this.onFinal(result);
            }
            
            // Check if we should return to KWS armed state
            if (this.enableKWS && this.kws && this.kwsReady) {
                // Small delay to allow final result to be processed
                setTimeout(() => {
                    this.handleASREndpoint();
                }, 100);
            }
        });
        
        this.currentEngine.setOnError((error) => {
            this.handleEngineError(error);
        });
        
        this.currentEngine.setOnStateChange((stateChange) => {
            this.handleStateChange(stateChange);
        });
    }

    /**
     * Handle engine errors with fallback
     */
    handleEngineError(error) {
        console.error('🎤 SmartSpeechEngine: Engine error:', error);
        
        // Handle streaming fallback specifically
        if (error.type === 'streaming_fallback_needed') {
            console.log('🎤 SmartSpeechEngine: Streaming fallback needed, switching to browser engine');
            this.switchEngine('browser').catch(err => {
                console.error('🎤 SmartSpeechEngine: Fallback switch failed:', err);
            });
            return;
        }

        // Classify benign no-speech/endpoint as normal endpoint, not error
        const message = (error && (error.error || error.message || '')).toString().toLowerCase();
        const benign = message.includes('no-speech') || message.includes('no speech') || message.includes('ended') || message.includes('aborted');
        if (benign) {
            // Transition listening -> endpointed -> ready (re-arm KWS)
            this.handleASREndpoint();
            return;
        }

        if (this.options.autoFallback && this.availableEngines.length > 1) {
            this.fallbackToNextEngine(error);
        }

        if (this.onError) {
            this.onError(error);
        }
    }

    /**
     * Fallback to next available engine
     */
    async fallbackToNextEngine(error) {
        const currentIndex = this.availableEngines.findIndex(
            eng => eng.engine === this.currentEngine
        );
        
        if (currentIndex >= 0 && currentIndex < this.availableEngines.length - 1) {
            const nextEngine = this.availableEngines[currentIndex + 1];
            
            console.log(`🔄 SmartSpeechEngine: Falling back from ${this.availableEngines[currentIndex].type} to ${nextEngine.type}`);
            
            // Stop current engine
            if (this.currentEngine) {
                await this.currentEngine.stop();
                this.currentEngine.destroy();
            }
            
            // Switch to next engine
            this.currentEngine = nextEngine.engine;
            this.setupEngineCallbacks();
            
            // Record fallback
            this.engineHistory.push({
                engine: nextEngine.type,
                timestamp: Date.now(),
                reason: 'fallback',
                previousEngine: this.availableEngines[currentIndex].type,
                error: error.error || error.message
            });
        }
    }

    /**
     * Handle state changes
     */
    handleStateChange(stateChange) {
        this.stateMachine.transitionTo(stateChange.current, {
            reason: 'engine_state_change',
            engine: this.getCurrentEngineType(),
            source: stateChange
        });
        
        if (this.onStateChange) {
            this.onStateChange(stateChange);
        }
    }

    /**
     * Get current engine type
     */
    getCurrentEngineType() {
        if (!this.currentEngine) return 'none';
        
        const engineMap = {
            'StreamingSTT': 'streaming',
            'VADSpeechEngine': 'vad',
            'BrowserSTT': 'browser',
            'MockSTT': 'mock'
        };
        
        return engineMap[this.currentEngine.constructor.name] || 'unknown';
    }

    /**
     * Start speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        // Don't allow start during engine switch
        if (this.isSwitching) {
            console.warn('🎤 SmartSpeechEngine: Cannot start during engine switch');
            return false;
        }
        
        try {
            // Initialize on first start (before engine availability check)
            if (!this.isInitialized) {
                await this.initialize();
            }

            if (!this.isSupported || !this.currentEngine) {
                console.error('🎤 SmartSpeechEngine: No engine available');
                return false;
            }

            // Check if we're already in ready state (KWS armed)
            const currentState = this.stateMachine.getCurrentState();
            if (currentState === MinimalStates.READY && this.enableKWS && this.kws && this.kwsReady) {
                console.log('🎤 SmartSpeechEngine: Already in KWS armed state, waiting for wake word...');
                return true;
            }
            if (currentState === MinimalStates.LISTENING) {
                // Idempotent start while already listening
                return true;
            }
            
            // Transition to acquiring_mic
            await this.stateMachine.transitionTo(MinimalStates.ACQUIRING_MIC, TransitionReasons.START_REQUESTED);
            
            // Start KWS if enabled
            if (this.enableKWS && this.kws && this.kwsReady) {
                console.log('🎤 SmartSpeechEngine: Starting KWS...');
                // Ensure AudioContext is running to satisfy autoplay policies
                const micManagerModule = await import('./MicManager.js');
                await micManagerModule.default.resume();
                const kwsStarted = await this.kws.start();
                
                if (kwsStarted) {
                    // Do NOT start main ASR here; KWS runs its own BrowserSTT for partials
                    // Transition to ready (KWS armed)
                    await this.stateMachine.transitionTo(MinimalStates.READY, TransitionReasons.KWS_ARMED);
                    console.log('🎤 SmartSpeechEngine: KWS armed, waiting for wake word...');
                    return true;
                } else {
                    console.warn('🎤 SmartSpeechEngine: KWS failed to start, falling back to direct ASR');
                }
            }
            
            // Fallback: Start ASR directly (no KWS or KWS failed)
            console.log(`🎤 SmartSpeechEngine: Starting ASR directly with ${this.getCurrentEngineType()} engine`);
            const result = await this.currentEngine.start();
            
            if (result) {
                // Transition to listening (direct ASR mode)
                await this.stateMachine.transitionTo(MinimalStates.LISTENING, TransitionReasons.LISTENING_STARTED);
            } else {
                // Transition back to idle on failure
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.ERROR_OCCURRED);
            }
            
            return result;
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Failed to start:', error);
            
            // If streaming engine fails, try to fallback to browser engine
            if (this.getCurrentEngineType() === 'streaming') {
                console.log('🎤 SmartSpeechEngine: Streaming engine failed, attempting fallback to browser engine...');
                try {
                    const fallbackResult = await this.switchEngine('browser');
                    if (fallbackResult) {
                        console.log('🎤 SmartSpeechEngine: Successfully fell back to browser engine');
                        return await this.start();
                    }
                } catch (fallbackError) {
                    console.error('🎤 SmartSpeechEngine: Fallback to browser engine also failed:', fallbackError);
                }
            }
            
            await this.stateMachine.transitionTo(MinimalStates.ERROR, TransitionReasons.ERROR_OCCURRED, { error });
            return false;
        }
    }

    /**
     * Stop speech recognition
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        // Don't allow stop during engine switch
        if (this.isSwitching) {
            console.warn('🎤 SmartSpeechEngine: Cannot stop during engine switch');
            return false;
        }
        
        if (!this.currentEngine) {
            return false;
        }

        try {
            // Check current state and handle appropriately
            const currentState = this.stateMachine.getCurrentState();
            
            if (currentState === MinimalStates.IDLE) {
                // Already idle, no need to stop
                return true;
            }
            
            if (currentState === MinimalStates.ERROR) {
                // In error state, try to stop the engine directly and reset to idle
                try {
                    await this.currentEngine.stop();
                    await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.STOPPING_COMPLETE);
                    return true;
                } catch (stopError) {
                    console.warn('🎤 SmartSpeechEngine: Failed to stop engine in error state:', stopError);
                    return false;
                }
            }
            
            // Normal stop flow
            await this.stateMachine.transitionTo(MinimalStates.STOPPING, TransitionReasons.STOP_REQUESTED);
            
            // Stop KWS if running
            if (this.kws && this.kwsReady) {
                try {
                    await this.kws.stop();
                    console.log('🎤 SmartSpeechEngine: KWS stopped');
                } catch (error) {
                    console.warn('🎤 SmartSpeechEngine: Failed to stop KWS:', error);
                }
            }
            
            // Stop current engine
            const result = await this.currentEngine.stop();
            
            // Transition to idle
            await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.STOPPING_COMPLETE);
            
            return result;
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Failed to stop:', error);
            // Try to force transition to idle for cleanup
            try {
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.ERROR_RECOVERED);
            } catch (transitionError) {
                console.warn('🎤 SmartSpeechEngine: Failed to transition to idle after stop error:', transitionError);
            }
            return false;
        }
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        return this.stateMachine.isInState(MinimalStates.LISTENING) || 
               (this.currentEngine ? this.currentEngine.isListening() : false);
    }
    
    /**
     * Check if engine switch is in progress
     * @returns {boolean} Switch status
     */
    isSwitchingEngines() {
        return this.isSwitching;
    }
    
    /**
     * Get pending switch target
     * @returns {string|null} Pending engine type or null
     */
    getPendingSwitch() {
        return this.pendingSwitch;
    }
    
    /**
     * Get engine switch status
     * @returns {Object} Switch status information
     */
    getSwitchStatus() {
        return {
            isSwitching: this.isSwitching,
            pendingSwitch: this.pendingSwitch,
            switchLock: this.switchLock,
            switchDuration: this.switchLock ? Date.now() - this.switchLock : null
        };
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        return this.stateMachine.getCurrentState();
    }

    /**
     * Check if speech recognition is supported
     * @returns {boolean} Support status
     */
    isSupported() {
        return this.isSupported;
    }

    /**
     * Get browser compatibility info
     * @returns {Object} Compatibility info
     */
    getBrowserInfo() {
        const baseInfo = this.currentEngine ? this.currentEngine.getBrowserInfo() : {};
        
        return {
            ...baseInfo,
            currentEngine: this.getCurrentEngineType(),
            availableEngines: this.availableEngines.map(eng => eng.type),
            engineHistory: this.engineHistory,
            isSmartEngine: true
        };
    }

    /**
     * Get engine statistics
     * @returns {Object} Engine statistics
     */
    getEngineStats() {
        return {
            currentEngine: this.getCurrentEngineType(),
            availableEngines: this.availableEngines.length,
            engineHistory: this.engineHistory,
            fallbackEnabled: this.options.autoFallback
        };
    }

    /**
     * Force switch to specific engine with strict protocol
     * @param {string} engineType - Engine type to switch to
     * @returns {Promise<boolean>} Success status
     */
    async switchEngine(engineType) {
        // Check if already switching
        if (this.isSwitching) {
            console.warn(`🎤 SmartSpeechEngine: Engine switch already in progress, queuing switch to ${engineType}`);
            this.pendingSwitch = engineType;
            return false;
        }
        
        const targetEngine = this.availableEngines.find(eng => eng.type === engineType);
        
        if (!targetEngine) {
            console.error(`🎤 SmartSpeechEngine: Engine ${engineType} not available`);
            return false;
        }
        
        // Don't switch to the same engine
        if (this.currentEngine && this.currentEngine.constructor.name === targetEngine.engine.constructor.name) {
            console.log(`🎤 SmartSpeechEngine: Already using ${engineType} engine`);
            return true;
        }
        
        try {
            console.log(`🎤 SmartSpeechEngine: Starting engine switch to ${engineType}`);
            
            // Step 1: Lock the switch
            this.isSwitching = true;
            this.switchLock = Date.now();
            
            // Step 2: Stop current engine with full cleanup
            if (this.currentEngine) {
                await this.stopCurrentEngine();
            }
            
            // Step 3: Reset state machine
            await this.resetStateMachine();
            
            // Step 4: Create fresh engine instance and start
            const freshEngine = await this.createEngine(engineType);
            if (!freshEngine) {
                throw new Error(`Failed to create fresh ${engineType} engine instance`);
            }
            
            this.currentEngine = freshEngine;
            this.setupEngineCallbacks();
            
            // Step 5: Record switch
            this.engineHistory.push({
                engine: engineType,
                timestamp: Date.now(),
                reason: 'manual_switch',
                switchDuration: Date.now() - this.switchLock
            });
            
            // Step 6: Unlock
            this.isSwitching = false;
            this.switchLock = null;
            
            console.log(`🎤 SmartSpeechEngine: Successfully switched to ${engineType} engine`);
            
            // Handle any pending switch
            if (this.pendingSwitch) {
                const pending = this.pendingSwitch;
                this.pendingSwitch = null;
                console.log(`🎤 SmartSpeechEngine: Processing pending switch to ${pending}`);
                return await this.switchEngine(pending);
            }
            
            return true;
        } catch (error) {
            console.error(`🎤 SmartSpeechEngine: Failed to switch to ${engineType}:`, error);
            
            // Cleanup on error
            this.isSwitching = false;
            this.switchLock = null;
            this.pendingSwitch = null;
            
            // Try to recover to a working state
            try {
                await this.resetStateMachine();
            } catch (resetError) {
                console.error('🎤 SmartSpeechEngine: Failed to reset state machine after switch error:', resetError);
            }
            
            return false;
        }
    }

    /**
     * Stop current engine with full cleanup
     * @private
     */
    async stopCurrentEngine() {
        if (!this.currentEngine) {
            return;
        }
        
        try {
            console.log('🎤 SmartSpeechEngine: Stopping current engine with full cleanup');
            
            // Stop the engine
            await this.currentEngine.stop();
            
            // Destroy the engine (this should clean up all resources)
            this.currentEngine.destroy();
            
            // Clear the reference
            this.currentEngine = null;
            
            console.log('🎤 SmartSpeechEngine: Current engine stopped and cleaned up');
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Error stopping current engine:', error);
            // Force clear the reference even if stop/destroy failed
            this.currentEngine = null;
            throw error;
        }
    }
    
    /**
     * Determine which speech engine type to use for VAD
     * Prefers streaming if available, falls back to browser
     * @returns {string} The speech engine type to use
     */
    getVADSpeechEngineType() {
        // Check if streaming engine is available in our available engines
        const streamingAvailable = this.availableEngines.some(eng => eng.type === 'streaming');
        
        if (streamingAvailable) {
            console.log('🎤 SmartSpeechEngine: VAD will use StreamingSTT engine');
            return 'streaming';
        } else {
            console.log('🎤 SmartSpeechEngine: VAD will use BrowserSTT engine (streaming not available)');
            return 'browser';
        }
    }

    /**
     * Reset state machine to idle state
     * @private
     */
    async resetStateMachine() {
        try {
            console.log('🎤 SmartSpeechEngine: Resetting state machine');
            
            // Force transition to idle state
            const currentState = this.stateMachine.getCurrentState();
            if (currentState !== MinimalStates.IDLE) {
                await this.stateMachine.transitionTo(MinimalStates.IDLE, TransitionReasons.ENGINE_SWITCH);
            }
            
            console.log('🎤 SmartSpeechEngine: State machine reset to idle');
        } catch (error) {
            console.error('🎤 SmartSpeechEngine: Error resetting state machine:', error);
            throw error;
        }
    }
    
    /**
     * Handle state changes from the state machine
     */
    handleStateChange(stateChange) {
        if (this.onStateChange) {
            this.onStateChange(stateChange);
        }
    }

    /**
     * Get KWS status for UI
     */
    getKWSStatus() {
        return {
            enabled: this.enableKWS,
            ready: this.kwsReady,
            wakePhrase: this.wakePhrase,
            lastScore: this.kws ? this.kws.getLastScore() : 0,
            type: this.kws ? this.kws.constructor.name : 'none'
        };
    }

    /**
     * Cleanup resources
     */
    destroy() {
        // Cancel any pending switch
        this.pendingSwitch = null;
        this.isSwitching = false;
        this.switchLock = null;
        
        this.stop();
        
        // Cleanup KWS
        if (this.kws) {
            try {
                this.kws.destroy();
                console.log('🎤 SmartSpeechEngine: KWS destroyed');
            } catch (error) {
                console.warn('🎤 SmartSpeechEngine: Failed to destroy KWS:', error);
            }
            this.kws = null;
            this.kwsReady = false;
        }
        
        if (this.currentEngine) {
            this.currentEngine.destroy();
        }
        
        // Cleanup all engines
        this.availableEngines.forEach(eng => {
            if (eng.engine !== this.currentEngine) {
                eng.engine.destroy();
            }
        });
        
        this.stateMachine.destroy();
        super.destroy();
    }
}
